import React from 'react';
import { useChat } from '@/context/ChatContext';
import { cn, formatDate, truncate } from '@/lib/utils';

export function Sidebar() {
  const {
    conversations,
    activeConversation,
    isLoadingConversations,
    createConversation,
    selectConversation,
    toggleFavorite,
    deleteConversation,
  } = useChat();

  const favorites = conversations.filter(c => c.is_favorite);
  const recent = conversations.filter(c => !c.is_favorite);

  return (
    <aside className="w-72 h-full bg-raised border-r border-zinc-800 flex flex-col">
      {/* New Chat Button */}
      <div className="p-4">
        <button
          onClick={() => createConversation()}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-primary text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
        >
          <span className="material-icons text-xl">add</span>
          New Chat
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {isLoadingConversations ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand border-t-transparent" />
          </div>
        ) : (
          <>
            {/* Favorites */}
            {favorites.length > 0 && (
              <div className="mb-4">
                <h3 className="px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Favorites
                </h3>
                {favorites.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isActive={activeConversation?.id === conv.id}
                    onSelect={() => selectConversation(conv.id)}
                    onToggleFavorite={() => toggleFavorite(conv.id)}
                    onDelete={() => deleteConversation(conv.id)}
                  />
                ))}
              </div>
            )}

            {/* Recent */}
            {recent.length > 0 && (
              <div>
                <h3 className="px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Recent
                </h3>
                {recent.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isActive={activeConversation?.id === conv.id}
                    onSelect={() => selectConversation(conv.id)}
                    onToggleFavorite={() => toggleFavorite(conv.id)}
                    onDelete={() => deleteConversation(conv.id)}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {conversations.length === 0 && (
              <div className="text-center py-8 px-4">
                <span className="material-icons text-4xl text-zinc-600 mb-2">chat_bubble_outline</span>
                <p className="text-zinc-500 text-sm">No conversations yet</p>
                <p className="text-zinc-600 text-xs mt-1">Start a new chat to begin</p>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

interface ConversationItemProps {
  conversation: {
    id: string;
    title: string | null;
    is_favorite: boolean;
    last_message_at: string | null;
    message_count: number;
  };
  isActive: boolean;
  onSelect: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onToggleFavorite,
  onDelete,
}: ConversationItemProps) {
  const [showMenu, setShowMenu] = React.useState(false);

  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200',
        isActive
          ? 'bg-zinc-800/80 text-white border-l-2 border-brand'
          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'
      )}
      onClick={onSelect}
    >
      <span className="material-icons text-lg opacity-60">chat</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">
          {truncate(conversation.title || 'New Chat', 25)}
        </p>
        {conversation.last_message_at && (
          <p className="text-xs text-zinc-600">
            {formatDate(conversation.last_message_at)}
          </p>
        )}
      </div>

      {/* Actions Menu */}
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-zinc-700 transition-opacity"
        >
          <span className="material-icons text-lg">more_vert</span>
        </button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 top-full mt-1 z-20 w-36 bg-elevated border border-zinc-700 rounded-lg shadow-lg py-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
              >
                <span className="material-icons text-lg">
                  {conversation.is_favorite ? 'star' : 'star_border'}
                </span>
                {conversation.is_favorite ? 'Unfavorite' : 'Favorite'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-zinc-700"
              >
                <span className="material-icons text-lg">delete</span>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
